"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { OfflineIndicator } from "@/components/offline/offline-indicator";
import { useBackgroundSync } from "@/hooks/use-background-sync";

interface OfflineContextType {
  isOnline: boolean;
  wasOffline: boolean;
  pendingCount: number;
  isSyncing: boolean;
  syncNow: () => Promise<void>;
  clearQueue: () => void;
  registerRequest: (url: string, method: string, options?: { body?: unknown; headers?: Record<string, string> }) => string;
}

const OfflineContext = createContext<OfflineContextType | null>(null);

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const [isOnline, setIsOnline] = useState(true);
  const [wasOffline, setWasOffline] = useState(false);
  const { pendingCount, isSyncing, syncNow, clearQueue, registerRequest } = useBackgroundSync();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setWasOffline(true);
      setTimeout(() => setWasOffline(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const value: OfflineContextType = {
    isOnline,
    wasOffline,
    pendingCount,
    isSyncing,
    syncNow,
    clearQueue,
    registerRequest,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
      <OfflineIndicator />
    </OfflineContext.Provider>
  );
}

export function useOffline(): OfflineContextType {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error("useOffline must be used within an OfflineProvider");
  }
  return context;
}